// NOTE: THIS FILE SHOULD NEVER HAVE ANY NON-TYPE DEFINITIONS
// WE IMPORT THIS FILE IN @/queries/constants SO IT IS VERY IMPORTANT
import type { TutorSessionFeedbackRating } from '@/queries/constants'

// NOTE: ORDER IS IMPORTANT. Highest level goes on top
// NOTE: Remember to create a migration to update get_user_progression_level
// SQL function if you change this
export const USER_LEVELS = [
  'level-diamond',
  'level-platinum',
  'level-gold',
  'level-silver',
  'level-bronze',
] as const

export const ACHIEVEMENT_NAMES = [
  'welcome',
  'attend-1',
  'attend-5',
  'attend-10',
  'attend-25',
  'attend-50',
  'attend-100',
  'attend-200',
  'certify-1',
  'certify-5',
  'certify-10',
  'certify-25',
  'certify-50',
  'certify-100',
  'host-1',
  'host-5',
  'host-10',
  'host-25',
  'host-50',
  'host-100',
  'host-200',
  'refer-1',
  'refer-5',
  'refer-10',
  'refer-25',
  'refer-50',
  'refer-100',
  ...USER_LEVELS,
  // Manually assigned
  'varun-birthday-2021',
  'allaam-birthday-2022',
  'kareena-birthday-2022',
  'summer-camp-2022',
  'summer-camp-2023-tutor',
  'summer-camp-2023-learner',
  'summer-camp-2024-tutor',
  'summer-camp-2024-learner',
  'summer-camp-2025-tutor',
  'summer-camp-2025-learner',
  'ms-pilot-2023',
  'subworld-pioneer-tutor-2023',
  'sat-referral-march-2024',
  'external-partnership-tutor',
] as const

export type AchievementId = (typeof ACHIEVEMENT_NAMES)[number]

export type AchievementDetail = {
  created_at: string
  displayed_at: string | null
}

export type UserAchievements = Partial<Record<AchievementId, AchievementDetail>>

export type UserProgression = {
  points: number

  num_sessions_attended: number
  num_sessions_hosted: number
  num_users_referred: number
  num_certifications_approved: number

  achievements: UserAchievements
}

export type AchievementCriteriaKey = keyof Omit<UserProgression, 'achievements'>

export type AchievementCategory =
  | 'attend'
  | 'certify'
  | 'host'
  | 'refer'
  | 'level'
  | 'one-off'
  | 'other'

export type Achievement = {
  title: string
  subtitle: string
  iconActive: string
  iconInactive: string
  criteriaKey: AchievementCriteriaKey
  criteriaValue: number
  category: AchievementCategory
  link?: string | null
}

interface UserEventBase {
  type: string
  payload: Record<string, unknown>
}

interface UserEventWelcome extends UserEventBase {
  type: 'welcome'
  payload: Record<string, never>
}

// This is something we award users in reward for something
// for one-time actions, no automation.
interface UserEventPointsEarned extends UserEventBase {
  type: 'points_earned'
  payload: {
    points: number
    note: string
  }
}

interface UserEventSessionAttended extends UserEventBase {
  type: 'session_attended'
  sessionId: number

  payload: {
    /**
     * @deprecated in favor of tutors attending Welcoming Team sessions
     */ asOnboardingBuddy?: boolean
    isRecommendedNewTutorOnboardingSession?: boolean
  }
}

interface UserEventCertificationApproved extends UserEventBase {
  type: 'certification_approved'
  payload: {
    certificationRequestId: number
    isCourseChallenge: boolean
  }
}

interface UserEventSessionHosted extends UserEventBase {
  type: 'session_hosted'
  sessionId: number
  payload: {
    hostsAttendedCount: number
    tutoringSeconds: number
  }
}

interface UserEventReferredUser extends UserEventBase {
  type: 'referred_user'
  payload: {
    userId: number
  }
}

interface UserEventReferredByUser extends UserEventBase {
  type: 'referred_by_user'
  payload: {
    userId: number
  }
}

interface UserEventSessionFeedbackGiven extends UserEventBase {
  type: 'session_feedback_given'
  sessionId: number
  payload: {
    // this field was added after the original type was created, so it should be present in future UserEvent's but not old ones
    sessionFeedbackId?: number
    isDetailed: boolean
    hasInternalComment?: boolean
    hasNps?: boolean
  }
}
export interface UserEventSessionFeedbackReceived extends UserEventBase {
  type: 'session_feedback_received'
  payload: {
    sessionFeedbackId: number
    rating: TutorSessionFeedbackRating.Helpful | TutorSessionFeedbackRating.SuperHelpful
  }
}

interface UserEventAchievementUnlocked extends UserEventBase {
  type: 'achievement_unlocked'
  payload: {
    achievementId: AchievementId
    note: string | null
  }
}

interface UserEventTutorTrainingCompleted extends UserEventBase {
  type: 'tutor_training_completed'
  payload: {
    trainingId: number
  }
}

interface UserEventCertificationReviewed extends UserEventBase {
  type: 'certification_reviewed'
  payload: {
    certificationReviewId: number
  }
}

interface UserEventSessionAudited extends UserEventBase {
  type: 'session_audited'
  payload: {
    tutorAuditId: number
  }
}

interface UserEventSessionSafetyAudited extends UserEventBase {
  type: 'session_safety_audited'
  payload: {
    tutorAuditId: number
  }
}

interface UserEventLearnerFeedbackGiven extends UserEventBase {
  type: 'learner_feedback_given'
  sessionId: number
  payload: Record<string, never>
}

interface UserEventLearnerFeedbackReceived extends UserEventBase {
  type: 'learner_feedback_received'
  sessionId: number
  payload: {
    reviewerUserId: number
  }
}

interface UserEventAuditReplySubmitted extends UserEventBase {
  type: 'audit_reply_submitted'
  payload: {
    auditId: number
  }
}

interface UserEventPostFeedbackGiven extends UserEventBase {
  type: 'post_feedback_given'
  payload: {
    postId: number
    isDetailed: boolean
    hasInternalComment: boolean
    worldSlug: string
  }
}

export interface UserEventPostFeedbackReceived extends UserEventBase {
  type: 'post_feedback_received'
  payload: {
    postId: number
    rating: 'Helpful' | 'Super Helpful'
    worldSlug: string
  }
}

export interface UserEventVerifyPeerReviewerLevel extends UserEventBase {
  type: 'verify_peer_reviewer_level'
  payload: {
    tutorProfileId: number
  }
}

export interface UserEventResolveCoachEscalation extends UserEventBase {
  type: 'resolve_coach_escalation'
  sessionId: number
  payload: Record<string, never>
}

export interface UserEventLearnerAccountDeleted extends UserEventBase {
  type: 'learner_account_deleted'
  payload: {
    country: string
  }
}

export type UserEventType =
  | UserEventWelcome
  | UserEventPointsEarned
  | UserEventSessionAttended
  | UserEventSessionHosted
  | UserEventReferredUser
  | UserEventReferredByUser
  | UserEventCertificationApproved
  | UserEventSessionFeedbackGiven
  | UserEventSessionFeedbackReceived
  | UserEventTutorTrainingCompleted
  | UserEventAchievementUnlocked
  | UserEventCertificationReviewed
  | UserEventSessionAudited
  | UserEventSessionSafetyAudited
  | UserEventLearnerFeedbackGiven
  | UserEventLearnerFeedbackReceived
  | UserEventAuditReplySubmitted
  | UserEventPostFeedbackGiven
  | UserEventPostFeedbackReceived
  | UserEventVerifyPeerReviewerLevel
  | UserEventResolveCoachEscalation
  | UserEventLearnerAccountDeleted

export type UserEventTypeWithPoints = UserEventType & { points: number }
